import React, { useEffect, useRef, useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * Robust audio player for interview recordings.
 *
 * WebM files produced by MediaRecorder don't write the duration into the
 * container headers (they are recorded as a "live"/streaming stream). As a
 * result a plain <audio> element reports duration = Infinity, gets stuck at
 * 0:00 and refuses to seek/play in Chrome, Brave and Firefox.
 *
 * The fix is the well-known "seek hack": once metadata loads, if the duration
 * is not a finite number we force currentTime to a huge value. The browser
 * then scans to the real end of the media, fires a `durationchange` with the
 * correct value, and afterwards playback/seeking work normally. We reset
 * currentTime back to 0 once the duration is resolved.
 *
 * If the browser still can't decode the codec (e.g. Safari with older
 * WebM/Opus), we surface a download link as a fallback so the file is never
 * unreachable.
 */
export const AudioPlayer = ({ src, name }) => {
  const [t] = useTranslation("investigation");
  const audioRef = useRef(null);
  const durationFixedRef = useRef(false);
  const [hasError, setHasError] = useState(false);

  // Reset internal state whenever the source changes (e.g. switching files
  // in the preview dialog without unmounting the component).
  useEffect(() => {
    durationFixedRef.current = false;
    setHasError(false);
  }, [src]);

  const handleLoadedMetadata = () => {
    const el = audioRef.current;
    if (!el) return;
    // Duration missing/invalid → trigger the seek hack once.
    if (
      (!Number.isFinite(el.duration) || el.duration === 0) &&
      !durationFixedRef.current
    ) {
      durationFixedRef.current = true;
      try {
        // Jumping past the end forces the browser to compute the real duration.
        el.currentTime = 1e101;
      } catch {
        // Some browsers throw if the media isn't seekable yet; ignore and let
        // durationchange/timeupdate retry below.
      }
    }
  };

  const handleDurationChange = () => {
    const el = audioRef.current;
    if (!el) return;
    // Once a finite duration is known, rewind to the start so the user plays
    // from the beginning.
    if (durationFixedRef.current && Number.isFinite(el.duration)) {
      durationFixedRef.current = false;
      try {
        el.currentTime = 0;
      } catch {
        // no-op
      }
    }
  };

  if (!src) {
    return <Typography>{t("Interview.no_audio_available_to_play")}</Typography>;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <audio
        ref={audioRef}
        src={src}
        controls
        preload="metadata"
        style={{ width: "100%" }}
        onLoadedMetadata={handleLoadedMetadata}
        onDurationChange={handleDurationChange}
        onError={() => setHasError(true)}
      >
        {/* Provide a <source> too so browsers that need it can sniff the type */}
        <source src={src} />
      </audio>

      {/* Fallback: if the browser can't decode the codec, offer a download. */}
      {hasError && (
        <Box sx={{ mt: 1.5, textAlign: "center" }}>
          <Typography sx={{ mb: 0.5 }} color="error" fontSize={14}>
            {t("Interview.audio_playback_not_supported")}
          </Typography>
          <Link
            href={src}
            download={name || "audio"}
            target="_blank"
            rel="noopener noreferrer"
          >
            {`${t("Interview.download")} ${name || ""}`.trim()}
          </Link>
        </Box>
      )}
    </Box>
  );
};
